/**
 * Мир класса `scalar` - первое из трёх поколений протокола состояния.
 *
 * Класс мира определяет схему, а не наоборот
 * (docs/research/game-specs/_FINAL_10_GAMES.md, раздел 2). `scalar` - это мир,
 * который целиком описывается фазой раунда, набором чисел, набором коротких
 * строк и несколькими короткими списками. Ни физики, ни координат, ни
 * населения: викторина, угадайка, перетягивание каната, заезд по дорожкам.
 *
 * ПОЧЕМУ У САМОГО ДЕШЁВОГО КЛАССА ВСЁ РАВНО ДЕЛЬТЫ. Соблазн отправлять снимок
 * велик - мир маленький. Но правила раздела 3.2 ARCHITECTURE введены не ради
 * `entities`: снимок в пять полей, отправленный двенадцать раз в секунду,
 * стоит столько же, сколько дельта в одно поле, отправленная один раз. Разница
 * в том, что второе - привычка, которая доживёт до `entities`, а первое -
 * привычка, которую там придётся ломать.
 *
 * ПРАВИЛА 3.2 В ЭТОМ КЛАССЕ:
 *
 *   1. Дельты вместо снимка       - `ScalarDelta`, снимок только по ресинку.
 *   2. Квантование до 0,1         - применяется публикатором ко всем числам.
 *   3. Экстраполяция на клиенте   - `phaseEndsAt` и `rates`: величина,
 *                                   меняющаяся предсказуемо, передаётся
 *                                   ЗАКОНОМ, а не значением. Таймер раунда
 *                                   стоит нуль байт в секунду.
 *   4. Бюджет пакета              - списки ограничены `cap`, журнал моментов
 *                                   не накапливается между пакетами.
 *   5. simTick и matchEpoch       - в конверте `StateFrame`.
 *   6. Интерполяция обязательна   - см. пункт 3.
 *   7. Частота событийная         - мир не изменился, пакета нет.
 */
import { quantize } from './codec.js';
/** Применяет операцию над списком. Общая для сервера и overlay: расхождения быть не может. */
export function applyListPatch(current, patch) {
    if ('clear' in patch) {
        return [];
    }
    if ('set' in patch) {
        return patch.set;
    }
    const merged = [...(current ?? []), ...patch.add];
    return merged.length > patch.cap ? merged.slice(merged.length - patch.cap) : merged;
}
/** Пустой мир: то, с чего начинается сцена до первого пакета. */
export function emptyScalarWorld() {
    return {
        phase: '',
        phaseEndsAt: null,
        round: 0,
        values: {},
        rates: {},
        text: {},
        lists: {},
    };
}
/**
 * Накладывает дельту на мир. Одна реализация на сервер и на overlay.
 *
 * Две реализации «наложить дельту» разошлись бы молча и проявились бы
 * расхождением картинки у зрителя с состоянием у сервера - тем самым классом
 * дефекта, который невозможно воспроизвести по жалобе.
 */
export function applyScalarDelta(world, delta) {
    const lists = { ...world.lists };
    for (const [key, patch] of Object.entries(delta.lists ?? {})) {
        lists[key] = applyListPatch(world.lists[key], patch);
    }
    return {
        phase: delta.phase ?? world.phase,
        phaseEndsAt: delta.phaseEndsAt === undefined ? world.phaseEndsAt : delta.phaseEndsAt,
        round: delta.round ?? world.round,
        values: { ...world.values, ...delta.values },
        rates: { ...world.rates, ...delta.rates },
        text: { ...world.text, ...delta.text },
        lists,
    };
}
/**
 * Склейка двух дельт в одну.
 *
 * ЗАЧЕМ. Между отправками мир меняется несколько раз: пришли три догадки и
 * сработало действие. Отправить четыре пакета вместо одного - это учетверить
 * накладные расходы кадра ради данных, которые всё равно применятся вместе.
 * Планировщик копит изменения и шлёт одно сообщение, а копить их можно только
 * умея складывать намерения.
 *
 * ПОЧЕМУ ЭТО НЕ ТРИВИАЛЬНО. Скаляры складываются затиранием - последнее
 * значение верно. Списки - нет: «очистить» плюс «дописать» - это «заменить», а
 * «дописать» плюс «дописать» - это «дописать оба с последним пределом». Написав
 * склейку как поверхностное слияние объектов, мы потеряли бы догадки, пришедшие
 * между двумя отправками, и не узнали бы об этом никогда: мир на сервере остался
 * бы верным, а у зрителя - нет.
 */
export function mergeScalarDelta(base, next) {
    const lists = { ...base.lists };
    for (const [key, patch] of Object.entries(next.lists ?? {})) {
        const previous = lists[key];
        if (previous === undefined || !('add' in patch)) {
            lists[key] = patch;
            continue;
        }
        if ('clear' in previous) {
            lists[key] = { set: applyListPatch([], patch) };
            continue;
        }
        if ('set' in previous) {
            lists[key] = { set: applyListPatch(previous.set, patch) };
            continue;
        }
        /**
         * ПРЕДЕЛ ПРИМЕНЯЕТСЯ ПРИ СКЛЕЙКЕ, А НЕ ТОЛЬКО ПРИ НАЛОЖЕНИИ.
         *
         * Найдено воротами трафика на первой же игре. Между двумя отправками
         * набегало восемьсот записей ленты, все они ехали в пакете, и все, кроме
         * последних восьми, клиент выбрасывал при наложении - предел-то
         * соблюдается там. Пакет весил шесть килобайт вместо трёхсот байт, и это
         * при пределе в восемь строк, объявленном самой игрой.
         *
         * Результат после наложения не меняется: `applyListPatch` всё равно
         * оставляет последние `cap`. Меняется только то, сколько мы за это платим
         * в канале - а «бюджет пакета - контракт, а не пожелание»
         * (docs/ARCHITECTURE.md, раздел 6).
         */
        const combined = [...previous.add, ...patch.add];
        lists[key] = {
            add: combined.length > patch.cap ? combined.slice(combined.length - patch.cap) : combined,
            cap: patch.cap,
        };
    }
    const merged = {};
    if (next.phase !== undefined || base.phase !== undefined) {
        merged.phase = next.phase ?? base.phase;
    }
    if (next.phaseEndsAt !== undefined) {
        merged.phaseEndsAt = next.phaseEndsAt;
    }
    else if (base.phaseEndsAt !== undefined) {
        merged.phaseEndsAt = base.phaseEndsAt;
    }
    if (next.round !== undefined || base.round !== undefined) {
        merged.round = next.round ?? base.round;
    }
    if (base.values !== undefined || next.values !== undefined) {
        merged.values = { ...base.values, ...next.values };
    }
    if (base.rates !== undefined || next.rates !== undefined) {
        merged.rates = { ...base.rates, ...next.rates };
    }
    if (base.text !== undefined || next.text !== undefined) {
        merged.text = { ...base.text, ...next.text };
    }
    if (Object.keys(lists).length > 0) {
        merged.lists = lists;
    }
    const beats = [...(base.beats ?? []), ...(next.beats ?? [])];
    if (beats.length > 0) {
        merged.beats = beats;
    }
    return merged;
}
/** Есть ли в дельте хоть что-нибудь. Пустая дельта не отправляется - правило 7. */
export function isEmptyDelta(delta) {
    return (delta.phase === undefined &&
        delta.phaseEndsAt === undefined &&
        delta.round === undefined &&
        delta.values === undefined &&
        delta.rates === undefined &&
        delta.text === undefined &&
        delta.lists === undefined &&
        delta.beats === undefined);
}
/**
 * Квантование всех чисел дельты - правило 2 раздела 3.2.
 *
 * ПО УМОЛЧАНИЮ, А НЕ ПО РЕШЕНИЮ РАЗРАБОТЧИКА ИГРЫ. В каталоге конкурента в
 * одной и той же роли сосуществуют три подхода: полная двойная точность (2 239
 * байт координат на пакет, до шестнадцати значащих цифр), округление до 0,1 и
 * целые. Разница - выбор автора игры, а не свойство протокола. Здесь выбора
 * нет: квантует публикатор, и игра об этом не знает.
 */
export function quantizeScalarDelta(delta, quantum) {
    const round = (value) => quantize(value, quantum);
    const mapNumbers = (source) => {
        if (source === undefined) {
            return undefined;
        }
        const result = {};
        for (const [key, value] of Object.entries(source)) {
            result[key] = round(value);
        }
        return result;
    };
    const mapEntries = (entries) => entries.map((entry) => ({ ...entry, values: entry.values.map(round) }));
    const lists = {};
    for (const [key, patch] of Object.entries(delta.lists ?? {})) {
        if ('set' in patch) {
            lists[key] = { set: mapEntries(patch.set) };
        }
        else if ('add' in patch) {
            lists[key] = { add: mapEntries(patch.add), cap: patch.cap };
        }
        else {
            lists[key] = patch;
        }
    }
    const values = mapNumbers(delta.values);
    const rates = mapNumbers(delta.rates);
    return {
        ...delta,
        ...(values === undefined ? {} : { values }),
        ...(rates === undefined ? {} : { rates }),
        ...(delta.lists === undefined ? {} : { lists }),
    };
}
//# sourceMappingURL=world-scalar.js.map